import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowsClockwise, ShieldCheck, Warning, Download, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PG8 Audit Report — firm-wide DocuSign compliance dashboard.
 *
 * Every TR1 / TP1 / DS1 envelope sent via ConveyHub is expected to
 * have a PG82 conveyancer's certificate on file. This page shows
 * one row per envelope with signer + witness + PG82 status so the
 * COLP / SRA reviewer can spot any deed that slipped through
 * without a certificate.
 *
 * Design decisions:
 *  - Quarter picker up top (default = current quarter). Solicitor
 *    can jump between quarters without re-loading.
 *  - Summary tiles first — compliance rate, TR1 count, missing PG82
 *    count. Red tile if the compliance rate < 100%.
 *  - Table below with one row per envelope, and a red row-background
 *    for TR1 envelopes missing a PG82. Ap1-bundle download button
 *    per row when the envelope is completed + tr1_mode.
 */

function currentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  return `${now.getUTCFullYear()}-Q${q}`;
}

function quarterOptions() {
  const now = new Date();
  const opts = [];
  let y = now.getUTCFullYear();
  let q = Math.floor(now.getUTCMonth() / 3) + 1;
  for (let i = 0; i < 8; i++) {
    opts.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return opts;
}

export default function Pg8AuditPage() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/docusign/audit/pg8", { params: { quarter } });
      setData(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load audit report");
    } finally {
      setLoading(false);
    }
  }, [quarter]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const downloadBundle = async (envelopeId) => {
    try {
      const resp = await API.get(`/docusign/envelopes/${envelopeId}/ap1-bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AP1 bundle - ${envelopeId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download AP1 bundle");
    }
  };

  const regeneratePg82 = async (envelopeId) => {
    try {
      await API.post(`/docusign/envelopes/${envelopeId}/pg82`);
      toast.success("PG82 regenerated");
      fetchAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate PG82");
    }
  };

  const summary = data?.summary || {};
  const complianceRate = summary.compliance_rate ?? 0;
  const isPerfect = complianceRate >= 100;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="pg8-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <ShieldCheck weight="fill" className="w-6 h-6 text-emerald-600" />
            Practice Guide 8 Audit
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            HMLR compliance ledger — every DocuSign deed sent by the firm, with PG82 certificate status per envelope.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="w-36" data-testid="pg8-quarter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {quarterOptions().map((q) => (
                <SelectItem key={q} value={q}>{q}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAudit} data-testid="pg8-refresh-btn">
            <ArrowsClockwise className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className={`p-3 ${isPerfect ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="pg8-tile-rate">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Compliance rate</div>
          <div className={`text-2xl font-bold mt-1 ${isPerfect ? "text-emerald-700" : "text-rose-700"}`}>
            {complianceRate}%
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {summary.pg82_auto_generated || 0} of {summary.tr1_completed || 0} TR1 completed
          </div>
        </Card>
        <Card className="p-3" data-testid="pg8-tile-total">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Envelopes sent</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{summary.total_envelopes || 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">this quarter</div>
        </Card>
        <Card className="p-3" data-testid="pg8-tile-tr1">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">TR1 deeds</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{summary.tr1_envelopes || 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Practice Guide 8 mode</div>
        </Card>
        <Card className="p-3" data-testid="pg8-tile-standard">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Standard e-sign</div>
          <div className="text-2xl font-bold text-indigo-700 mt-1">{summary.standard_envelopes || 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Non-deed docs</div>
        </Card>
        <Card className={`p-3 ${(summary.pg82_missing || 0) > 0 ? "bg-rose-50 border-rose-200" : ""}`} data-testid="pg8-tile-missing">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Missing PG82</div>
          <div className={`text-2xl font-bold mt-1 ${(summary.pg82_missing || 0) > 0 ? "text-rose-700" : "text-slate-400"}`}>
            {summary.pg82_missing || 0}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Requires regeneration</div>
        </Card>
      </div>

      {err && (
        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-md mb-3" data-testid="pg8-error">
          {err}
        </div>
      )}

      {/* Envelope table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Sent</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-left px-3 py-2">Signer</th>
                <th className="text-left px-3 py-2">Witness</th>
                <th className="text-left px-3 py-2">SMS auth</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">PG82</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
              )}
              {!loading && (data?.envelopes || []).length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">No envelopes in this window.</td></tr>
              )}
              {!loading && (data?.envelopes || []).map((e) => {
                const missingPg82 = e.tr1_mode && (e.status || "").toLowerCase() === "completed" && !e.pg82_generated_at;
                return (
                  <tr key={e.envelope_id} data-testid={`pg8-row-${e.envelope_id}`}
                      className={`border-t border-slate-100 ${missingPg82 ? "bg-rose-50" : ""}`}>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString("en-GB") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {e.tr1_mode ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">TR1 · PG8</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">Standard</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{e.signer_name || "—"}</div>
                      <div className="text-slate-500 text-[10px]">{e.signer_email}</div>
                    </td>
                    <td className="px-3 py-2">
                      {e.witness_name ? (
                        <>
                          <div className="text-slate-900">{e.witness_name}</div>
                          <div className="text-slate-500 text-[10px]">{e.witness_email}</div>
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {e.signer_phone_masked ? (
                        <span className="text-slate-700 font-mono text-[11px]">{e.signer_phone_masked}</span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] uppercase">{e.status || "—"}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {e.pg82_generated_at ? (
                        <span className="text-emerald-700 text-[11px]">
                          ✓ {new Date(e.pg82_generated_at).toLocaleDateString("en-GB")}
                        </span>
                      ) : e.tr1_mode ? (
                        <span className="text-rose-700 text-[11px] inline-flex items-center gap-1">
                          <Warning weight="fill" className="w-3 h-3" /> Missing
                        </span>
                      ) : (
                        <span className="text-slate-400 text-[11px]">n/a</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {(e.status || "").toLowerCase() === "completed" && e.tr1_mode && (
                        <>
                          <Button
                            size="sm" variant="outline"
                            data-testid={`pg8-bundle-${e.envelope_id}`}
                            onClick={() => downloadBundle(e.envelope_id)}
                            className="mr-1 h-7 text-[11px]"
                            title="Download AP1 bundle (signed deed + audit cert + PG82)"
                          >
                            <Download className="w-3 h-3 mr-1" /> AP1
                          </Button>
                          {missingPg82 && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`pg8-regen-${e.envelope_id}`}
                              onClick={() => regeneratePg82(e.envelope_id)}
                              className="h-7 text-[11px]"
                            >
                              <FileArrowDown className="w-3 h-3 mr-1" /> Gen PG82
                            </Button>
                          )}
                        </>
                      )}
                      {e.case_id && (
                        <Link to={`/cases/${e.case_id}`}
                              className="inline-block ml-1 text-[11px] text-indigo-600 hover:underline">
                          Case →
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
